'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import SearchFiltersBar from '@/components/shared/SearchFiltersBar';
import DatePickerPopover from '@/components/shared/DatePickerPopover';
import GuestPickerPopover from '@/components/shared/GuestPickerPopover';
import { calculateDeparture } from '@/lib/dateUtils';
import {
  trackAvailabilityEvent,
  type SearchScope,
  type SearchSource,
} from '@/lib/analytics/posthog';

interface ChildAge {
  id: string;
  age: '4-' | '5+';
}

export interface AvailabilitySearchFormProps {
  /** Define qué inventario buscar al submitear */
  scope?: SearchScope;
  /** Slug del edificio (ej: 'nuevo-conquistador'). Solo aplica con scope='building' */
  buildingId?: string;
  /** Nombre del barrio (ej: 'El Laguito'). Solo aplica con scope='neighborhood' */
  neighborhood?: string;
  /** Etiqueta de contexto opcional sobre el formulario (ej: "Edificio Nuevo Conquistador") */
  contextLabel?: string;
  /** ARIA label del botón de búsqueda (ícono lupa). Default: "Consultar disponibilidad" */
  ctaLabel?: string;
  /** Origen del submit para analítica */
  source: SearchSource;
  /** Clase adicional sobre el contenedor del form */
  className?: string;
  /** Pre-selección de fechas/huéspedes (opcional, útil al rehidratar desde URL) */
  initialArrival?: string;
  initialNights?: number;
  initialNumAdults?: number;
}

/**
 * Construye la URL de `/reservas` con los parámetros canónicos.
 * Centraliza la lógica para que home, edificios y barrios produzcan URLs idénticas.
 */
export function buildAvailabilitySearchURL(params: {
  arrival: string;
  departure: string;
  numAdults: number;
  scope?: SearchScope;
  buildingId?: string;
  neighborhood?: string;
  source: SearchSource;
}): string {
  const search = new URLSearchParams({
    arrival: params.arrival,
    departure: params.departure,
    numAdults: String(params.numAdults),
  });
  if (params.scope === 'building' && params.buildingId) {
    search.set('building', params.buildingId);
  } else if (params.scope === 'neighborhood' && params.neighborhood) {
    search.set('neighborhood', params.neighborhood);
  }
  search.set('source', params.source);
  return `/reservas?${search.toString()}`;
}

/**
 * Formulario unificado de búsqueda de disponibilidad.
 *
 * Reusa `SearchFiltersBar` + `DatePickerPopover` + `GuestPickerPopover` y centraliza
 * la lógica de validación + redirect a `/reservas`. Es la versión COMPACTA del
 * buscador (pill bar). El home mantiene su versión hero más visual en
 * `UnifiedSearchBar`, pero ambos comparten `buildAvailabilitySearchURL` para
 * que la forma de la URL sea idéntica.
 *
 * Comportamiento:
 *   - Si no hay fechas o son pasadas, abre el date picker y marca error.
 *   - Submit navega a /reservas con building/neighborhood/source preservados.
 *   - Dispara `availability_search_submit` y `availability_datepicker_open` en PostHog.
 */
export default function AvailabilitySearchForm({
  scope = 'all',
  buildingId,
  neighborhood,
  contextLabel,
  ctaLabel,
  source,
  className = '',
  initialArrival = '',
  initialNights = 3,
  initialNumAdults = 2,
}: AvailabilitySearchFormProps) {
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const guestButtonRef = useRef<HTMLButtonElement>(null);

  const [today, setToday] = useState('');
  useEffect(() => {
    setToday(new Date().toLocaleDateString('en-CA'));
  }, []);

  const [arrival, setArrival] = useState(initialArrival);
  const [nights, setNights] = useState(initialNights);
  const [numAdults, setNumAdults] = useState(initialNumAdults);
  const [children, setChildren] = useState<ChildAge[]>([]);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState('Selecciona una fecha de entrada válida');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalAdults = useMemo(() => {
    const children5Plus = children.filter((c) => c.age === '5+').length;
    return numAdults + children5Plus;
  }, [numAdults, children]);

  const smallChildren = useMemo(
    () => children.filter((c) => c.age === '4-').length,
    [children]
  );

  const departure = useMemo(
    () => calculateDeparture(arrival, nights),
    [arrival, nights]
  );

  const toggleDatePicker = () => {
    const willOpen = !showDatePicker;
    setShowDatePicker(willOpen);
    setShowGuestPicker(false);
    setDateError(false);
    setDateErrorMessage('Selecciona una fecha de entrada válida');
    if (willOpen) {
      trackAvailabilityEvent('availability_datepicker_open', {
        source,
        scope,
        building_id: buildingId,
        neighborhood,
      });
    }
  };

  const toggleGuestPicker = () => {
    setShowGuestPicker((v) => !v);
    setShowDatePicker(false);
  };

  // Lógica de submit reusable: form submit (lupa) + Listo del guest picker.
  // Bug fix 2026-05-05 (agente SEO): el "Listo" de huéspedes no disparaba
  // búsqueda — el cliente debía clickear lupa después. Ahora ambos comparten
  // esta función.
  const submitSearch = () => {
    if (isSubmitting) return;
    setDateError(false);
    setDateErrorMessage('Selecciona una fecha de entrada válida');

    if (!arrival || !departure) {
      setDateError(true);
      setDateErrorMessage('Selecciona fecha de llegada y salida');
      setShowDatePicker(true);
      return;
    }
    if (nights < 2) {
      setDateError(true);
      setDateErrorMessage('Selecciona al menos 2 noches');
      setShowDatePicker(true);
      return;
    }
    if (today && arrival < today) {
      setDateError(true);
      setDateErrorMessage('La fecha de llegada no puede ser en el pasado');
      setShowDatePicker(true);
      return;
    }

    trackAvailabilityEvent('availability_search_submit', {
      source,
      scope,
      building_id: buildingId,
      neighborhood,
      arrival,
      departure,
      num_adults: totalAdults,
    });

    const url = buildAvailabilitySearchURL({
      arrival,
      departure,
      numAdults: totalAdults,
      scope,
      buildingId,
      neighborhood,
      source,
    });

    // Feedback inmediato: deshabilita el botón y muestra spinner mientras
    // Next.js negocia el route change. Cierra popovers antes de navegar para
    // evitar parpadeo si el usuario los tiene abiertos.
    setIsSubmitting(true);
    setShowDatePicker(false);
    setShowGuestPicker(false);
    router.push(url);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitSearch();
  };

  const handleDateConfirm = () => {
    setShowDatePicker(false);
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      setShowGuestPicker(true);
    }, 0);
  };

  useEffect(() => {
    const handleOpenDatePicker = (event: Event) => {
      const detail = (event as CustomEvent<{ targetId?: string }>).detail;
      if (detail?.targetId) {
        const target = document.getElementById(detail.targetId);
        if (target && formRef.current && !target.contains(formRef.current)) return;
      }

      setShowGuestPicker(false);
      setDateError(false);
      setDateErrorMessage('Selecciona una fecha de entrada válida');
      setShowDatePicker(true);
      trackAvailabilityEvent('availability_datepicker_open', {
        source,
        scope,
        building_id: buildingId,
        neighborhood,
      });
    };

    window.addEventListener('pacartagena:open-availability-datepicker', handleOpenDatePicker);
    return () => {
      window.removeEventListener('pacartagena:open-availability-datepicker', handleOpenDatePicker);
    };
  }, [buildingId, neighborhood, scope, source]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={`w-full max-w-3xl mx-auto relative ${className}`}
      aria-label={ctaLabel ?? 'Consultar disponibilidad'}
    >
      {contextLabel && (
        <p className="mb-3 text-center text-[11px] md:text-xs font-semibold uppercase tracking-wider text-gray-500">
          {contextLabel}
        </p>
      )}

      <SearchFiltersBar
        arrival={arrival}
        departure={departure}
        nights={nights}
        onToggleDatePicker={toggleDatePicker}
        dateError={dateError}
        dateButtonRef={dateButtonRef}
        totalAdults={totalAdults}
        smallChildren={smallChildren}
        onToggleGuestPicker={toggleGuestPicker}
        guestButtonRef={guestButtonRef}
        isLoading={isSubmitting}
      />

      <DatePickerPopover
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onConfirm={handleDateConfirm}
        arrival={arrival}
        nights={nights}
        onArrivalChange={(v) => {
          setArrival(v);
          setDateError(false);
          setDateErrorMessage('Selecciona una fecha de entrada válida');
        }}
        onNightsChange={(value) => {
          setNights(value);
          if (value >= 2) {
            setDateError(false);
            setDateErrorMessage('Selecciona una fecha de entrada válida');
          }
        }}
        minNights={2}
        error={dateError}
        errorMessage={dateErrorMessage}
        className="md:left-2"
        getAnchorEl={() => dateButtonRef.current}
        desktopPreferredPlacement="top"
      />

      <GuestPickerPopover
        isOpen={showGuestPicker}
        onClose={() => setShowGuestPicker(false)}
        onConfirm={() => {
          setShowGuestPicker(false);
          submitSearch();
        }}
        numAdults={numAdults}
        onNumAdultsChange={setNumAdults}
        onChildrenChange={setChildren}
        initialChildren={children}
        className="md:left-1/2"
        getAnchorEl={() => guestButtonRef.current}
      />
    </form>
  );
}
